/**
 * The trade ticket's view-model: everything the ticket renders, derived from the order SDK's
 * `SheetState` plus a little greenbook state (the stake text being typed, the send in flight,
 * the unknown-outcome lookup). Pure — no React, no I/O — so the derivation is unit-tested
 * (ticketModel.test.ts) and the dev review page (/dev/ticket) can feed mocked sheet states
 * through the exact same code the live ticket uses.
 *
 * The SDK stays the engine (venue rules, pricing, placement, the real-money confirm). This
 * module only decides what to SAY about it: greenbook's copy for every issue code, which field
 * a problem sits under, the one-tap fix, the Real-mode "venue not ready" advice, and the
 * result screens. Spec: docs/ticket-ux.md.
 */
import type {
  AccountFacts,
  Issue,
  OrderSheetError,
  PlaceLeg,
  PlaceResult,
  Quote,
  SheetIssueCode,
  SheetState,
  StakeUnit,
  TrackedOrder,
  VenueOption,
} from "@openmarketsai/order-sheet";
import { fmtCents, fmtMoney, fmtPrice, type OddsFormat } from "./format.js";
import { phaseFromSheet } from "./orderFlow.js";

export type Mode = "paper" | "real";

/* ================================================================== fixes */

/** A one-tap fix. The SDK applies the stake / price / venue ones; the rest are greenbook's. */
export type FixAction =
  | { kind: "setStake"; unit: StakeUnit; value: number }
  | { kind: "setMaxPrice"; value: number }
  | { kind: "autoPrice" }
  | { kind: "select"; partnerId: string }
  /** greenbook's unlock explainer (plan / trade scope). */
  | { kind: "unlock" }
  /** app.openmarkets.ai — where venues are connected and funded. */
  | { kind: "venueAccounts" }
  | { kind: "paper" }
  | { kind: "real" }
  | { kind: "market" }
  /** Re-read the position + rules (`sheet.refresh()`). */
  | { kind: "retry" }
  | { kind: "openOptions" };

export interface FixVM {
  label: string;
  action: FixAction;
  /** Opens another site. */
  external?: boolean;
}

/* ================================================================ problems */

/** Which field a problem sits under. `general` = above the button. */
export type Anchor = "stake" | "price" | "venue" | "general";

export interface ProblemVM {
  /** Stable identity (anchor + code) for the debounced display. */
  key: string;
  anchor: Anchor;
  tone: "block" | "warn" | "info";
  message: string;
  fixes: FixVM[];
  /** Raw SDK/API code(s) — tooltip only, never shown as text. */
  code: string;
}

/* ================================================================== the VM */

export interface ChipVM {
  label: string;
  value: number;
  active: boolean;
}

export interface VenueRowVM {
  partnerId: string;
  name: string;
  logoUrl: string | null;
  priceText: string;
  best: boolean;
  selected: boolean;
  blocked: boolean;
  /** One line: why it's greyed out. */
  reason: string | null;
  /** Real mode only. */
  readiness: Readiness | null;
  balanceText: string | null;
}

export type Readiness = "ready" | "connect" | "fund";

export interface QuoteVM {
  /** `none` = nothing to price yet. `checking` softens + pulses the numbers. */
  status: "none" | "estimate" | "checking" | "exact";
  pay: string;
  win: string;
  profit: string;
  profitTone: "up" | "down" | "dim";
  details: Array<{ label: string; value: string }>;
}

export interface StepVM {
  label: string;
  state: "done" | "active" | "todo" | "failed" | "unknown";
}

export type ResultVM =
  | {
      kind: "filled";
      headline: string;
      line: string;
      win: string;
      paper: boolean;
      /** A REAL leg booked somewhere other than the venue named. */
      warning: string | null;
      details: Array<{ label: string; value: string }>;
    }
  | {
      kind: "resting";
      headline: string;
      filledPct: number;
      filledText: string;
      /** False when we only know about the order from the lookup (the sheet can't cancel it). */
      canCancel: boolean;
      cancelBusy: boolean;
      cancelNote: string | null;
      details: Array<{ label: string; value: string }>;
    }
  | {
      kind: "partial";
      headline: string;
      filledPct: number;
      rest: { amount: number; price: number; label: string } | null;
      details: Array<{ label: string; value: string }>;
    }
  | {
      kind: "declined";
      headline: string;
      retry: { price: number; label: string };
      details: Array<{ label: string; value: string }>;
      code: string;
    }
  | { kind: "cancelled"; headline: string; line: string | null }
  | { kind: "checking"; headline: string }
  | { kind: "unresolved"; headline: string; note: string };

export interface OverlayVM {
  steps: StepVM[];
  /** Null while the order is still in flight. */
  result: ResultVM | null;
}

export interface ButtonVM {
  label: string;
  tone: Mode;
  disabled: boolean;
  spinner: boolean;
}

export interface ConfirmVM {
  spend: string;
  outcome: string;
  priceText: string;
  win: string;
  confirmLabel: string;
}

export interface TicketVM {
  mode: Mode;
  header: {
    title: string;
    sub: string | null;
    venueName: string;
    venueLogoUrl: string | null;
    priceText: string;
  };
  /** Real mode only: the picked venue's account state. */
  connection: { readiness: Readiness; text: string; fix: FixVM | null } | null;
  /** "Polymarket has it at 30¢ — Switch". Display only; the user switches. */
  hint: { text: string; fix: FixVM } | null;
  /** First load: the stake/quote area shimmers. */
  loading: boolean;
  stake: { unit: StakeUnit; text: string; label: string; chips: ChipVM[]; disabled: boolean };
  quote: QuoteVM;
  options: {
    summary: string;
    priceText: string;
    priceLabel: string;
    priceHint: string | null;
    priceInvalid: boolean;
    priceIsAuto: boolean;
    canLimit: boolean;
    orderType: "market" | "limit";
    orderTypeCopy: string;
    verifyLiquidity: boolean;
    venues: VenueRowVM[];
  };
  /** Every problem, ordered by importance; the view shows one per anchor. */
  problems: ProblemVM[];
  button: ButtonVM;
  confirm: ConfirmVM | null;
  overlay: OverlayVM | null;
  /** The form can't be edited (sending / result / unknown). */
  locked: boolean;
}

/* =============================================================== the input */

/** What was sent, captured at the moment of the tap (the sheet's state moves on after it). */
export interface SendSnapshot {
  at: number;
  mode: Mode;
  partnerId: string;
  venueName: string;
  liquidityHash: string | null;
  contestId: string | null;
  title: string | null;
  stake: { unit: StakeUnit; value: number };
  cost: number;
  contracts: number;
  maxPrice: number;
  practiceBookId: string | null;
}

/** The unknown-outcome lookup (see matchSentOrder). */
export type LookupState =
  | { status: "checking"; attempts: number }
  | { status: "found"; row: OrderRowLike }
  | { status: "unresolved"; note: string };

/** The subset of SheetState the ticket reads (the dev page mocks exactly this). */
export type SheetView = Pick<
  SheetState,
  | "phase"
  | "venues"
  | "venue"
  | "mode"
  | "orderType"
  | "stake"
  | "maxPrice"
  | "maxPriceIsAuto"
  | "quote"
  | "validation"
  | "preflightStatus"
  | "result"
  | "orders"
  | "error"
  | "account"
  | "canPlace"
  | "hint"
  | "loading"
>;

export interface TicketInput {
  sheet: SheetView;
  /** The sheet has finished its first load (later loads are quiet refreshes). */
  everLoaded: boolean;
  oddsFormat: OddsFormat;
  /** The clicked cell — the header renders from it before the SDK has loaded. */
  clicked: {
    title: string;
    sideKey?: string | null;
    marketLabel?: string | null;
    venueName: string;
    venueLogoUrl?: string | null;
    price: number | null;
    americanOdds?: string | null;
  };
  unit: StakeUnit;
  stakeText: string;
  /** Max price as typed, in cents; null = the SDK's auto ceiling. */
  priceText: string | null;
  verifyLiquidity: boolean;
  /** greenbook's limit setting AND the venue serving `limit`. */
  canLimit: boolean;
  /** A thrown, non-retryable place/confirm error (the request was refused, nothing placed). */
  placeError: { code: string; message: string } | null;
  /** The thrown error is a plan/grant refusal — the ticket shows the unlock card instead. */
  entitlementBlocked: boolean;
  send: SendSnapshot | null;
  /** 0 = "Sending", 1 = "At <venue>" (a short timer in the container). */
  sendStep: 0 | 1;
  lookup: LookupState | null;
  cancel: { busy: boolean; note: string | null };
  /** The venue's `failure_reason` for a declined order, looked up from the order row. */
  declineReason?: string | null;
  /** Account-partners fallback for a venue the constraints didn't describe. */
  readinessOf?: (partnerId: string) => { connected: boolean; balance: number | null } | undefined;
}

/* ============================================================== formatting */

/** "$1", "$3.10" — chips and fix labels drop ".00". */
export function fmtStakeMoney(n: number): string {
  return Number.isInteger(Math.round(n * 100) / 100) && Math.abs(n - Math.round(n)) < 0.005
    ? `$${Math.round(n)}`
    : fmtMoney(n);
}

function fmtContracts(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/0$/, "");
}

function fmtBalance(n: number | null, currency: string | null | undefined): string {
  if (n == null) return "—";
  if ((currency ?? "").toUpperCase() === "ATLAS") return `${Math.floor(n).toLocaleString()} ATLAS`;
  return fmtMoney(n);
}

const ceilCents = (n: number) => Math.ceil(n * 100 - 1e-6) / 100;
const floorCents = (n: number) => Math.floor(n * 100 + 1e-6) / 100;
const round2 = (n: number) => Math.round(n * 100) / 100;

/** Parse a typed stake; null for "", "0", "0." and garbage. */
export function parseStake(text: string): number | null {
  const n = Number(text.trim());
  return text.trim() !== "" && Number.isFinite(n) && n > 0 ? n : null;
}

/** Typed max price in cents ("33", "33.5") → probability, or null when not a valid price. */
export function parseCents(text: string): number | null {
  const n = Number(text.trim());
  if (text.trim() === "" || !Number.isFinite(n) || n <= 0 || n >= 100) return null;
  return Math.round(n * 10) / 1000;
}

/** Probability → the cents the max-price field shows ("33", "33.5"). */
export function centsText(p: number): string {
  const c = Math.round(p * 1000) / 10;
  return Number.isInteger(c) ? String(c) : c.toFixed(1);
}

/* ============================================================ stake memory */

/** Quick-pick chips: dollars in Amount mode, contracts in Shares mode. */
export const AMOUNT_CHIPS = [1, 5, 10, 25] as const;
export const SHARE_CHIPS = [5, 10, 25, 100] as const;

export const DEFAULT_STAKE: Record<StakeUnit, string> = { amount: "2", shares: "10" };

export interface RememberedStake {
  unit: StakeUnit;
  text: Record<StakeUnit, string>;
}

const STAKE_KEY = "gb.ticket.stake";

type StorageLike = Pick<Storage, "getItem" | "setItem">;

function storage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** The last stake used in this mode (per browser), or greenbook's default. */
export function readStake(mode: Mode, store: StorageLike | null = storage()): RememberedStake {
  const fallback: RememberedStake = { unit: "amount", text: { ...DEFAULT_STAKE } };
  try {
    const raw = store?.getItem(STAKE_KEY);
    if (!raw) return fallback;
    const all = JSON.parse(raw) as Partial<Record<Mode, { unit?: unknown; amount?: unknown; shares?: unknown }>>;
    const m = all?.[mode];
    if (!m || typeof m !== "object") return fallback;
    const ok = (v: unknown): v is string => typeof v === "string" && parseStake(v) != null;
    return {
      unit: m.unit === "shares" ? "shares" : "amount",
      text: { amount: ok(m.amount) ? m.amount : DEFAULT_STAKE.amount, shares: ok(m.shares) ? m.shares : DEFAULT_STAKE.shares },
    };
  } catch {
    return fallback;
  }
}

/** Remember the stake that was just sent, for this mode only. Never throws. */
export function writeStake(mode: Mode, unit: StakeUnit, text: string, store: StorageLike | null = storage()): void {
  if (parseStake(text) == null) return;
  try {
    const raw = store?.getItem(STAKE_KEY);
    const all = (raw ? JSON.parse(raw) : {}) as Record<string, Record<string, string>>;
    const prev = all[mode] && typeof all[mode] === "object" ? all[mode] : {};
    all[mode] = { ...prev, unit, [unit]: text.trim() };
    store?.setItem(STAKE_KEY, JSON.stringify(all));
  } catch {
    /* private mode / quota: the stake just isn't remembered */
  }
}

/* ================================================================== venues */

/** The picked venue's real-money readiness: connected first, then funded. */
export function readinessOf(v: VenueOption, fallback?: TicketInput["readinessOf"]): { readiness: Readiness; balance: number | null } {
  const fb = !v.constraints ? fallback?.(v.partnerId) : undefined;
  const connected = fb ? fb.connected : v.account.connected;
  const balance = fb ? fb.balance : (v.account.expectedBalance ?? v.account.balance);
  if (!connected) return { readiness: "connect", balance };
  if (balance != null && balance <= 0.005) return { readiness: "fund", balance };
  return { readiness: "ready", balance };
}

/**
 * Spec §5 — Real mode, the picked venue isn't connected or has no money:
 *   - another connected, funded venue quotes this market → suggest switching to it (cheapest);
 *   - none does → offer to try it in paper;
 *   - either way, the Connect / Add funds action for the picked venue.
 * Null when the venue is ready (or in paper, which needs no venue account).
 */
export function realVenueAdvice(
  mode: Mode,
  venue: VenueOption | null,
  venues: VenueOption[],
  oddsFormat: OddsFormat,
  fallback?: TicketInput["readinessOf"],
): ProblemVM | null {
  if (mode !== "real" || !venue) return null;
  const { readiness } = readinessOf(venue, fallback);
  if (readiness === "ready") return null;

  const alt = venues
    .filter(
      (v) =>
        v.partnerId !== venue.partnerId &&
        v.price != null &&
        v.actionable !== false &&
        v.eligibility.status !== "blocked" &&
        readinessOf(v, fallback).readiness === "ready",
    )
    .sort((a, b) => (a.price ?? 1) - (b.price ?? 1))[0];

  const own: FixVM =
    readiness === "connect"
      ? { label: `Connect ${venue.name}`, action: { kind: "venueAccounts" }, external: true }
      : { label: "Add funds", action: { kind: "venueAccounts" }, external: true };
  const what = readiness === "connect" ? `${venue.name} isn't connected.` : `${venue.name} has no funds.`;

  if (alt) {
    return {
      key: "venue:not_ready",
      anchor: "venue",
      tone: "block",
      message: `${what} ${alt.name} has this at ${fmtPrice(alt.price, alt.americanOdds, oddsFormat)}`,
      fixes: [{ label: `Switch to ${alt.name}`, action: { kind: "select", partnerId: alt.partnerId } }, own],
      code: readiness === "connect" ? "venue_disabled" : "fund_venue",
    };
  }
  return {
    key: "venue:not_ready",
    anchor: "venue",
    tone: "block",
    message: `${what} None of your connected venues offer this market.`,
    fixes: [{ label: "Try it in paper", action: { kind: "paper" } }, own],
    code: readiness === "connect" ? "venue_disabled" : "fund_venue",
  };
}

/** The cheapest other venue a user could actually switch to in this mode. */
function bestAlternative(state: SheetView, fallback?: TicketInput["readinessOf"]): VenueOption | null {
  const venue = state.venue;
  return (
    state.venues
      .filter(
        (v) =>
          v.partnerId !== venue?.partnerId &&
          v.price != null &&
          v.eligibility.status !== "blocked" &&
          (state.mode === "paper" || readinessOf(v, fallback).readiness === "ready"),
      )
      .sort((a, b) => (a.price ?? 1) - (b.price ?? 1))[0] ?? null
  );
}

/* ============================================================ issue → copy */

/** Codes greenbook never shows as a problem (handled elsewhere, or noise). */
const HIDDEN: ReadonlySet<SheetIssueCode> = new Set<SheetIssueCode>([
  "confirm_required", // the REAL MONEY card
  "better_price_available", // the venue hint
  "constraints_stale", // permanent on 0.1.1 — see FRICTION.md
  "stale_quote", // re-checked quietly (spec §5)
  "not_ready", // the loading state
  "no_stake", // the button reads "Enter a stake"
]);

/** Every code the SDK can raise → the field it concerns. */
export const ISSUE_ANCHOR: Record<SheetIssueCode, Anchor> = {
  // 0.1.2: a placement whose outcome couldn't be confirmed (retrying replays, never double-places).
  outcome_unknown: "general",
  real_money_not_permitted: "general",
  practice_not_permitted: "general",
  test_mode_requires_paper: "general",
  venue_disabled: "venue",
  orders_not_supported: "venue",
  scope_not_granted: "general",
  execution_limit_exceeded: "stake",
  insufficient_balance: "stake",
  venue_shard_underfunded: "venue",
  below_min_order: "stake",
  above_max_order: "stake",
  unfillable: "stake",
  below_min_contracts: "stake",
  amount_off_increment: "stake",
  price_out_of_band: "price",
  above_max_price: "price",
  price_off_tick: "price",
  insufficient_size: "stake",
  insufficient_live_depth: "venue",
  no_viable_venues: "venue",
  no_live_price_for_paper: "venue",
  invalid_requested_amount: "stake",
  halted_upstream: "general",
  unknown: "general",
  no_venue: "venue",
  no_stake: "stake",
  no_quote: "venue",
  paper_limit_unsupported: "general",
  order_type_unsupported: "general",
  mode_unavailable: "general",
  real_money_disabled: "general",
  not_paper_bettable: "general",
  stale_quote: "venue",
  better_price_available: "venue",
  constraints_stale: "general",
  confirm_required: "general",
  confirm_expired: "general",
  not_ready: "general",
};

interface CopyCtx {
  state: SheetView;
  unit: StakeUnit;
  oddsFormat: OddsFormat;
  canTradeReal: boolean;
  fallback?: TicketInput["readinessOf"];
}

/** A stake fix in the unit being typed: dollars, or the contracts those dollars buy. */
function stakeFix(label: string, usd: number, cx: CopyCtx, roundUp: boolean): FixVM {
  const price = refPrice(cx.state);
  if (cx.unit === "shares" && price) {
    const shares = roundUp ? Math.ceil(usd / price - 1e-9) : Math.floor(usd / price + 1e-9);
    return { label: label.replace(/\$[\d,.]+/, `${shares}`), action: { kind: "setStake", unit: "shares", value: Math.max(1, shares) } };
  }
  return { label, action: { kind: "setStake", unit: "amount", value: round2(usd) } };
}

function refPrice(s: SheetView): number | null {
  return s.orderType === "limit" ? s.maxPrice : (s.venue?.price ?? s.quote?.price ?? null);
}

function balanceFor(s: SheetView): { amount: number | null; currency: string | null } {
  if (s.mode === "paper") return { amount: s.account?.practiceWallet.balance ?? null, currency: s.account?.practiceWallet.currency ?? null };
  const a = s.venue?.account;
  return { amount: a ? (a.expectedBalance ?? a.balance) : null, currency: a?.currency ?? "USD" };
}

/**
 * greenbook's copy for one SDK issue, with its fix. Numbers come from the venue's served
 * rules on the sheet (the Issue object itself carries only the SDK's sentence); when a number
 * isn't known the SDK/server sentence is used as-is.
 */
export function problemFor(issue: Issue, cx: CopyCtx): ProblemVM | null {
  if (HIDDEN.has(issue.code)) return null;
  const s = cx.state;
  const v = s.venue;
  const c = v?.constraints ?? null;
  const name = v?.name ?? "This venue";
  const price = refPrice(s);
  const anchor = ISSUE_ANCHOR[issue.code] ?? (issue.scope === "venue" ? "venue" : "general");
  const tone = issue.severity;
  const make = (message: string, fixes: FixVM[] = []): ProblemVM => ({
    key: `${anchor}:${issue.code}`,
    anchor,
    tone,
    message,
    fixes,
    code: issue.detail ? `${issue.code} — ${issue.detail}` : issue.code,
  });
  const alt = bestAlternative(s, cx.fallback);
  const switchAlt: FixVM[] = alt ? [{ label: `Switch to ${alt.name}`, action: { kind: "select", partnerId: alt.partnerId } }] : [];
  const cents = (p: number) => fmtPrice(p, null, cx.oddsFormat === "american" ? "cents" : cx.oddsFormat);

  switch (issue.code) {
    case "real_money_not_permitted":
      return make("Real-money trading isn't on your plan yet.", [
        { label: "Unlock", action: { kind: "unlock" } },
        { label: "Try it in paper", action: { kind: "paper" } },
      ]);
    case "practice_not_permitted":
      return make("Paper trading isn't available on this account.");
    case "test_mode_requires_paper":
      return make("This is a test key — it can only place paper orders.", [{ label: "Switch to paper", action: { kind: "paper" } }]);
    case "venue_disabled":
      return make(`${name} isn't connected on your account.`, [
        ...switchAlt,
        { label: `Connect ${name}`, action: { kind: "venueAccounts" }, external: true },
      ]);
    case "orders_not_supported":
      return make(`${name} only shows prices — it doesn't take orders.`, switchAlt.length ? switchAlt : [{ label: "Other venues", action: { kind: "openOptions" } }]);
    case "scope_not_granted":
      return make("greenbook isn't allowed to place orders for you yet.", [{ label: "Unlock", action: { kind: "unlock" } }]);
    case "execution_limit_exceeded": {
      const cap = s.account?.policy?.max_stake_per_order ?? null;
      return cap != null
        ? make(`Your trading limits cap an order at ${fmtMoney(cap)}.`, [stakeFix(`Use ${fmtStakeMoney(cap)}`, cap, cx, false)])
        : make(issue.detail ? `Your trading limits block this: ${issue.detail}.` : "Your trading limits block this order.");
    }
    case "insufficient_balance": {
      const bal = balanceFor(s);
      if (bal.amount != null && bal.amount > 0.005) {
        const usable = floorCents(bal.amount);
        const fixes: FixVM[] = [stakeFix(`Use ${fmtStakeMoney(usable)}`, usable, cx, false)];
        if (s.mode === "real") fixes.push({ label: "Add funds", action: { kind: "venueAccounts" }, external: true });
        return make(`You only have ${fmtBalance(bal.amount, bal.currency)}`, fixes);
      }
      return make(
        s.mode === "paper" ? "Your paper wallet is empty." : `You have no funds on ${name}.`,
        s.mode === "real" ? [{ label: "Add funds", action: { kind: "venueAccounts" }, external: true }] : [],
      );
    }
    case "venue_shard_underfunded":
      return make(`Your ${name} balance can't cover this market right now.`, [
        { label: "Add funds", action: { kind: "venueAccounts" }, external: true },
      ]);
    case "below_min_order":
      return c
        ? make(`${name} needs at least ${fmtStakeMoney(c.size.min_usd)}`, [stakeFix(`Set ${fmtStakeMoney(c.size.min_usd)}`, c.size.min_usd, cx, true)])
        : make(issue.message);
    case "above_max_order":
      return c?.size.max_usd != null
        ? make(`${name} takes at most ${fmtStakeMoney(c.size.max_usd)}`, [stakeFix(`Set ${fmtStakeMoney(c.size.max_usd)}`, c.size.max_usd, cx, false)])
        : make(issue.message);
    case "unfillable":
      if (price) {
        const one = ceilCents(price);
        return make(`That doesn't buy one contract at ${cents(price)}`, [stakeFix(`Set ${fmtStakeMoney(one)}`, one, cx, true)]);
      }
      return make(issue.message);
    case "below_min_contracts": {
      const n = Math.max(1, c?.size.min_contracts ?? 1);
      if (price) {
        const usd = ceilCents(n * price);
        return make(`${name} needs at least ${n} contract${n === 1 ? "" : "s"}`, [
          cx.unit === "shares"
            ? { label: `Set ${n}`, action: { kind: "setStake", unit: "shares", value: n } }
            : { label: `Set ${fmtStakeMoney(usd)}`, action: { kind: "setStake", unit: "amount", value: usd } },
        ]);
      }
      return make(issue.message);
    }
    case "amount_off_increment": {
      const inc = c?.size.increment_usd ?? null;
      const stake = s.stake.value;
      if (inc && stake) {
        const down = Math.floor(stake / inc + 1e-9) * inc;
        return make(`${name} takes steps of ${fmtStakeMoney(inc)} — this rounds to ${fmtStakeMoney(down)}`, [
          { label: `Set ${fmtStakeMoney(down)}`, action: { kind: "setStake", unit: "amount", value: round2(down) } },
        ]);
      }
      return make(issue.message);
    }
    case "price_out_of_band": {
      if (c && s.maxPrice != null) {
        const clamp = s.maxPrice < c.price.min ? c.price.min : c.price.max;
        return make(`${name} accepts ${cents(c.price.min)}–${cents(c.price.max)}`, [
          { label: `Set ${cents(clamp)}`, action: { kind: "setMaxPrice", value: clamp } },
        ]);
      }
      return make(issue.message, [{ label: "Use auto", action: { kind: "autoPrice" } }]);
    }
    case "above_max_price": {
      const now = v?.price ?? null;
      if (now != null && s.maxPrice != null) {
        return make(`The price moved to ${cents(now)}, past your ${cents(s.maxPrice)} max`, [
          { label: `Raise max to ${cents(now)}`, action: { kind: "setMaxPrice", value: now } },
        ]);
      }
      return make("The price moved past your max.", [{ label: "Use auto", action: { kind: "autoPrice" } }]);
    }
    case "price_off_tick":
      return make(
        c?.price.tick ? `${name} prices in ${cents(c.price.tick)} steps — your max will be rounded.` : `${name} will round your max to its price steps.`,
      );
    case "insufficient_size": {
      const avail = v?.available ?? null;
      if (avail != null && avail > 0.005) {
        const usable = floorCents(avail);
        return make(`Only ${fmtMoney(usable)} is available at ${price ? cents(price) : "this price"}`, [
          stakeFix(`Use ${fmtStakeMoney(usable)}`, usable, cx, false),
          ...switchAlt,
        ]);
      }
      return make(issue.message, switchAlt);
    }
    case "insufficient_live_depth":
      return make(`Nothing's available at this price on ${name} right now.`, switchAlt.length ? switchAlt : [{ label: "Re-check", action: { kind: "retry" } }]);
    case "no_viable_venues":
      return make(`${name} isn't quoting this right now.`, switchAlt.length ? switchAlt : [{ label: "Re-check", action: { kind: "retry" } }]);
    case "no_live_price_for_paper":
      return make("There's no live price to paper-trade against right now.", switchAlt.length ? switchAlt : [{ label: "Re-check", action: { kind: "retry" } }]);
    case "invalid_requested_amount":
      return make("Enter a stake above $0.");
    case "halted_upstream":
      return make("Not placed — another leg failed first.");
    case "unknown":
      return make(issue.message || "The venue declined this order.", [{ label: "Re-check", action: { kind: "retry" } }]);
    case "no_venue":
      return make("Pick a venue to trade on.", [{ label: "Venues", action: { kind: "openOptions" } }]);
    case "no_quote":
      return make(`${name} has no price for this right now.`, switchAlt.length ? switchAlt : [{ label: "Re-check", action: { kind: "retry" } }]);
    case "paper_limit_unsupported":
      // 0.1.2 reads `practice_wallet.order_types`: this account's paper wallet takes no limits.
      return make("Limit orders aren't available in paper on this account.", [{ label: "Use market", action: { kind: "market" } }]);
    case "order_type_unsupported":
      return make(`${name} doesn't take this order type.`, [{ label: "Use market", action: { kind: "market" } }]);
    case "mode_unavailable":
      return s.mode === "real"
        ? make("Real-money trading isn't available on this account.", [{ label: "Try it in paper", action: { kind: "paper" } }])
        : make("Paper trading isn't available on this account.");
    case "real_money_disabled":
      return make("Real money isn't switched on in greenbook.", [{ label: "Switch to paper", action: { kind: "paper" } }]);
    case "not_paper_bettable":
      return make("This one can't be paper-traded yet.", [
        cx.canTradeReal ? { label: "Switch to Real", action: { kind: "real" } } : { label: "Unlock Real", action: { kind: "unlock" } },
      ]);
    case "confirm_expired":
      return make("The order changed — check it and place it again.");
    default:
      return make(issue.message);
  }
}

/**
 * greenbook's copy for a `blocked` placement's issues as one line, for the compact single-order
 * confirms (Market making's Get out / Move bid / custom hedge). Hidden codes are dropped; null
 * when nothing is left to say.
 */
export function issueText(
  issues: readonly Issue[],
  state: SheetView,
  oddsFormat: OddsFormat,
  canTradeReal = true,
  unit: StakeUnit = state.stake.unit,
): string | null {
  const cx: CopyCtx = { state, unit, oddsFormat, canTradeReal };
  const seen = new Set<string>();
  const out: string[] = [];
  for (const i of [...issues].sort((a, b) => TONE_RANK[a.severity] - TONE_RANK[b.severity])) {
    const m = problemFor(i, cx)?.message;
    if (m && !seen.has(m)) {
      seen.add(m);
      out.push(m.replace(/[.\s]+$/, ""));
    }
  }
  return out.length ? `${out.join(". ")}.` : null;
}

const TONE_RANK = { block: 0, warn: 1, info: 2 } as const;

/**
 * Every problem the ticket could show, in priority order: the Real-mode venue advice first
 * (it replaces the SDK's own connect/fund issues for the picked venue), then the sheet's
 * issues (blocking → warning → info), then thrown errors.
 */
export function deriveProblems(input: TicketInput, canTradeReal = true): ProblemVM[] {
  const s = input.sheet;
  const out: ProblemVM[] = [];
  const advice = realVenueAdvice(s.mode, s.venue, s.venues, input.oddsFormat, input.readinessOf);
  if (advice) out.push(advice);

  const cx: CopyCtx = { state: s, unit: input.unit, oddsFormat: input.oddsFormat, canTradeReal, fallback: input.readinessOf };
  const seen = new Set(out.map((p) => p.key));
  const issues = [...s.validation.all].sort((a, b) => TONE_RANK[a.severity] - TONE_RANK[b.severity]);
  for (const i of issues) {
    // The advice already speaks for the picked venue's account.
    if (advice && (i.code === "venue_disabled" || i.fix === "connect_venue" || (i.fix === "fund_venue" && i.code !== "insufficient_balance"))) continue;
    if (advice && i.code === "insufficient_balance" && readinessOf(s.venue!, input.readinessOf).readiness === "fund") continue;
    const p = problemFor(i, cx);
    if (p && !seen.has(p.key)) {
      seen.add(p.key);
      out.push(p);
    }
  }

  if (input.priceText != null && parseCents(input.priceText) == null) {
    out.unshift({ key: "price:invalid", anchor: "price", tone: "block", message: "Enter a max price between 1¢ and 99¢.", fixes: [{ label: "Use auto", action: { kind: "autoPrice" } }], code: "invalid_max_price" });
  }

  if (input.placeError && !input.entitlementBlocked) {
    out.push({ key: "general:place_error", anchor: "general", tone: "block", message: input.placeError.message, fixes: [], code: input.placeError.code });
  }
  const err = s.error;
  if (err && !input.entitlementBlocked && !input.placeError && !input.send) {
    // A failed preflight leaves `preflightStatus: 'error'`; a failed load leaves it untouched.
    if (s.preflightStatus === "error") {
      out.push({ key: "general:check_error", anchor: "general", tone: "warn", message: "Couldn't check the exact price.", fixes: [{ label: "Re-check", action: { kind: "retry" } }], code: errCode(err) });
    } else {
      out.push({ key: "general:load_error", anchor: "general", tone: "block", message: "Couldn't load this market.", fixes: [{ label: "Try again", action: { kind: "retry" } }], code: errCode(err) });
    }
  }
  return out;
}

function errCode(e: OrderSheetError): string {
  return `${e.code}${e.status ? ` (${e.status})` : ""} — ${e.message}`;
}

/**
 * The debounced display (spec §2 "Timing"). While the user is mid-edit, a stake or price
 * problem that ISN'T already on screen stays hidden, so a half-typed "0." never flashes
 * "below minimum"; problems already showing stay while they still apply, and one the edit
 * fixed disappears at once. Venue and general problems aren't caused by typing and always
 * show. The button never waits for this — it reads `canPlace` directly.
 */
export function gateProblems(all: ProblemVM[], shown: ProblemVM[], typing: boolean): ProblemVM[] {
  if (!typing) return all;
  const visible = new Set(shown.map((p) => p.key));
  return all.filter((p) => (p.anchor !== "stake" && p.anchor !== "price") || visible.has(p.key));
}

/** The one problem shown under a field: the first (most important) for that anchor. */
export function problemAt(problems: ProblemVM[], anchor: Exclude<Anchor, "general">): ProblemVM | null {
  return problems.find((p) => p.anchor === anchor) ?? null;
}

/** General problems (above the button), at most two. */
export function generalProblems(problems: ProblemVM[]): ProblemVM[] {
  return problems.filter((p) => p.anchor === "general").slice(0, 2);
}

/* ================================================================== quote */

function quoteVM(input: TicketInput): QuoteVM {
  const s = input.sheet;
  const q = s.quote;
  if (!q) return { status: "none", pay: "—", win: "—", profit: "—", profitTone: "dim", details: [] };
  const { pay, win } = payWin(q);
  const refreshing = s.loading && input.everLoaded;
  // From the edit until the server answers: the sheet auto-preflights any valid input
  // (~300 ms debounce), so an unanswered, valid quote is "checking", not an estimate.
  const checking =
    refreshing || s.preflightStatus === "pending" || (q.freshness !== "preflight" && s.validation.ok && s.preflightStatus !== "error");
  const status: QuoteVM["status"] = q.freshness === "preflight" && !refreshing && s.preflightStatus !== "pending" ? "exact" : checking ? "checking" : "estimate";
  const details: QuoteVM["details"] = [
    { label: "Contracts", value: fmtContracts(q.contracts) },
    { label: "Avg price", value: fmtCents(q.price) },
  ];
  if (q.fee) details.push({ label: q.fee.estimated ? "Fee (est.)" : "Fee", value: fmtMoney(q.fee.taker) });
  if (q.stake.unit === "amount" && q.leftover > 0.005) details.push({ label: "Unused stake", value: fmtMoney(q.leftover) });
  details.push({ label: s.orderType === "limit" ? "Limit" : "Max price", value: fmtCents(q.maxPrice) });
  if (q.wouldRest) details.push({ label: "Fills", value: "rests on the book until filled" });
  return {
    status,
    pay: fmtMoney(pay),
    win: fmtMoney(win),
    profit: `${q.profit >= 0 ? "+" : "−"}${fmtMoney(Math.abs(q.profit))}`,
    profitTone: q.profit > 0.005 ? "up" : q.profit < -0.005 ? "down" : "dim",
    details,
  };
}

/** What you pay and could win: the fee lands on whichever side the venue charges it. */
export function payWin(q: Pick<Quote, "cost" | "payout" | "fee">): { pay: number; win: number } {
  const fee = q.fee?.taker ?? 0;
  if (q.fee?.direction === "from_payout") return { pay: q.cost, win: round2(q.payout - fee) };
  return { pay: round2(q.cost + fee), win: q.payout };
}

/* ================================================================= results */

const PRICE_MOVED: ReadonlySet<string> = new Set(["above_max_price", "insufficient_live_depth", "unfillable", "insufficient_size", "price_out_of_band"]);

/** The price a retry should go at: the venue's current price if it moved up, else one tick up. */
export function retryPrice(sentMax: number, nowPrice: number | null, tick: number | null): number {
  const step = tick && tick > 0 ? tick : 0.01;
  const next = nowPrice != null && nowPrice > sentMax + 1e-9 ? nowPrice : sentMax + step;
  return Math.min(0.99, Math.round(next * 1000) / 1000);
}

function legDetails(result: PlaceResult | null, legs: PlaceLeg[], nameOf: (id: string | null) => string): Array<{ label: string; value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  for (const l of legs) {
    if (l.routerOrderIds.length) out.push({ label: "Order", value: l.routerOrderIds.join(", ") });
    if (l.routedPartnerIds.length) out.push({ label: "Routed to", value: l.routedPartnerIds.map(nameOf).join(", ") });
    if (l.sorDecisionId) out.push({ label: "SOR decision", value: l.sorDecisionId });
    if (l.issue) out.push({ label: "Reason", value: l.issue.detail ?? l.issue.code });
  }
  if (result?.idempotencyKey) out.push({ label: "Idempotency key", value: result.idempotencyKey });
  return out;
}

function fillPct(orders: TrackedOrder[], legs: PlaceLeg[]): { pct: number; filled: number; total: number } {
  if (orders.length) {
    const filled = orders.reduce((n, o) => n + (o.filledAmt || 0), 0);
    const total = orders.reduce((n, o) => n + (o.filledAmt || 0) + (o.unfilledAmt || 0), 0);
    return { pct: total > 0 ? Math.min(1, filled / total) : 0, filled, total };
  }
  const filled = legs.reduce((n, l) => n + l.fill.notional, 0);
  const total = legs.reduce((n, l) => n + l.fill.notional + l.fill.unfilled, 0);
  return { pct: total > 0 ? Math.min(1, filled / total) : 0, filled, total };
}

/** The result screen for a placement the sheet knows about. Null while nothing has landed. */
export function resultFromSheet(input: TicketInput): ResultVM | null {
  const s = input.sheet;
  const send = input.send;
  const r = s.result;
  if (!r || !send) return null;
  const venueName = send.venueName;
  const price = (p: number) => fmtPrice(p, null, input.oddsFormat);
  const nameOf = (id: string | null) => (id ? (s.venues.find((v) => v.partnerId === id)?.name ?? (id === send.practiceBookId ? "paper book" : id)) : "—");
  const legs = r.legs;
  const failedLeg = legs.find((l) => l.status === "failed" || l.status === "rejected" || l.issue != null);
  const shares = legs.reduce((n, l) => n + l.fill.shares, 0);
  const notional = legs.reduce((n, l) => n + l.fill.notional, 0);
  const avg = legs.find((l) => l.fill.avgPrice != null)?.fill.avgPrice ?? null;
  const details = legDetails(r, legs, nameOf);
  // phaseFromSheet already tells a whole-contract remainder (a fill) from a declined leg.
  const flow = phaseFromSheet(s.phase, r, null, s.orders);

  if (flow === "cancelled") {
    return { kind: "cancelled", headline: "Order cancelled", line: shares > 0 ? `${fmtContracts(round2(shares))} contracts had already filled — they're in Portfolio.` : null };
  }

  if (flow === "resting") {
    const f = fillPct(s.orders, legs);
    return {
      kind: "resting",
      headline: s.phase === "pending" ? `Confirming at ${venueName}` : `Waiting at ${price(send.maxPrice)} on ${venueName}`,
      filledPct: f.pct,
      filledText: f.total > 0 ? `${fmtMoney(f.filled)} of ${fmtMoney(f.total)} filled` : "Nothing filled yet",
      canCancel: s.orders.some((o) => o.fillStatus !== "filled" && !o.settled && o.status !== "cancelled"),
      cancelBusy: input.cancel.busy,
      cancelNote: input.cancel.note,
      details,
    };
  }

  if (flow === "failed") {
    const next = retryPrice(send.maxPrice, s.venue?.price ?? null, s.venue?.constraints?.price.tick ?? null);
    if (shares <= 1e-9) {
      const code = failedLeg?.issue?.code ?? "unknown";
      const moved = PRICE_MOVED.has(code);
      // The leg's own issue first; else the venue's `failure_reason` from the order row (the
      // SDK's tracked orders don't carry it, so the container looks it up: `declineReason`).
      const said = failedLeg?.issue?.message ?? input.declineReason ?? null;
      const reason = moved ? `price moved past ${price(send.maxPrice)}` : lowerFirst(stripDot(said ?? "the venue declined this order"));
      return {
        kind: "declined",
        headline: `${venueName} declined: ${reason}`,
        retry: moved ? { price: next, label: `Retry at ${price(next)}` } : { price: send.maxPrice, label: "Retry" },
        details,
        code: failedLeg?.issue ? `${failedLeg.issue.code}${failedLeg.issue.detail ? ` — ${failedLeg.issue.detail}` : ""}` : (failedLeg?.status ?? s.phase),
      };
    }
    // Some filled, the rest was declined.
    const unfilled = legs.reduce((n, l) => n + l.fill.unfilled, 0);
    const of = send.contracts > 0 ? send.contracts : shares;
    return {
      kind: "partial",
      headline: `Got ${fmtContracts(round2(shares))} of ${fmtContracts(round2(of))} contracts — the price moved`,
      filledPct: of > 0 ? Math.min(1, shares / of) : 0,
      rest: unfilled > 0.005 ? { amount: round2(unfilled), price: next, label: `Try the rest at ${price(next)}` } : null,
      details,
    };
  }

  if (flow !== "filled") return null;
  const sub = legs.find((l) => l.substituted);
  // A resting order that filled later: the fill is on the tracked orders, not the original legs.
  const trackedFill = s.orders.reduce((n, o) => n + (o.filledAmt || 0), 0);
  let fShares = shares;
  let fNotional = notional;
  let fAvg = avg;
  if (trackedFill > notional + 0.005) {
    fAvg = s.orders.find((o) => o.avgPrice != null)?.avgPrice ?? avg ?? send.maxPrice;
    fNotional = trackedFill;
    fShares = fAvg > 0 ? trackedFill / fAvg : 0;
  }
  return {
    kind: "filled",
    headline: "Order filled",
    line: `${fmtContracts(round2(fShares))} contracts${fAvg != null ? ` @ ${price(fAvg)}` : ""} · ${fmtMoney(fNotional)}`,
    win: `Could win ${fmtMoney(fShares)}`,
    paper: send.mode === "paper",
    warning: sub ? `Routed to ${nameOf(sub.routedPartnerIds[0] ?? null)}, not ${venueName} — report this.` : null,
    details,
  };
}

/** The result screen for an order found by the unknown-outcome lookup. */
export function resultFromRow(row: OrderRowLike, send: SendSnapshot, oddsFormat: OddsFormat): ResultVM {
  const price = (p: number) => fmtPrice(p, null, oddsFormat);
  const filled = row.filled_amt ?? 0;
  const unfilled = row.unfilled_amt ?? 0;
  const p = row.price ?? send.maxPrice;
  const details = [
    { label: "Order", value: row.router_order_id },
    { label: "Found by", value: "matching your recent orders (the request timed out)" },
  ];
  const status = String(row.status ?? "").toLowerCase();
  if (row.fill_status === "filled") {
    const shares = p > 0 ? filled / p : 0;
    return {
      kind: "filled",
      headline: "Order filled",
      line: `${fmtContracts(round2(shares))} contracts @ ${price(p)} · ${fmtMoney(filled)}`,
      win: `Could win ${fmtMoney(shares)}`,
      paper: send.mode === "paper",
      warning: null,
      details,
    };
  }
  if (status === "failed" || status === "rejected" || status === "cancelled" || status === "canceled") {
    return {
      kind: "declined",
      headline: `${send.venueName} declined: ${lowerFirst(stripDot(String(row.failure_reason ?? "the order didn't go through")))}`,
      retry: { price: send.maxPrice, label: "Retry" },
      details,
      code: status,
    };
  }
  const total = filled + unfilled;
  return {
    kind: "resting",
    headline: `Waiting at ${price(p)} on ${send.venueName}`,
    filledPct: total > 0 ? filled / total : 0,
    filledText: total > 0 ? `${fmtMoney(filled)} of ${fmtMoney(total)} filled` : "Nothing filled yet",
    canCancel: false,
    cancelBusy: false,
    cancelNote: "Found in your orders — cancel it from Orders.",
    details,
  };
}

function stripDot(s: string): string {
  return s.replace(/[.\s]+$/, "");
}
function lowerFirst(s: string): string {
  return s && /^[A-Z][a-z]/.test(s) ? s[0].toLowerCase() + s.slice(1) : s;
}

/* ====================================================== unknown outcome */

/** The blotter fields the matcher reads (`GET /flow/v1/auth/orders` rows, observed live). */
export interface OrderRowLike {
  router_order_id: string;
  partner_id?: string | null;
  contest_id?: string | null;
  liquidity_hash?: string | null;
  title?: string | null;
  amount?: number | null;
  price?: number | null;
  status?: string | null;
  fill_status?: string | null;
  filled_amt?: number | null;
  unfilled_amt?: number | null;
  currency?: string | null;
  funding?: { is_real_money?: boolean | null; currency?: string | null } | null;
  partner?: { internal_book?: boolean | null } | null;
  mm_strategy_id?: string | null;
  failure_reason?: string | null;
  created_at?: string | null;
  placed_datetime?: string | null;
  [k: string]: unknown;
}

/**
 * A liquidity hash is `<partner>:<position_hash>` (undocumented; the order SDK's own
 * `loadPosition` splits it exactly this way). The part after the partner names the position on
 * any book — which is what a paper order booked on the internal practice book shares with the
 * venue's cell you clicked.
 */
export function positionKeyOf(liquidityHash: string | null | undefined): string | null {
  if (!liquidityHash) return null;
  const i = liquidityHash.indexOf(":");
  return i > 0 ? liquidityHash.slice(i + 1) : null;
}

/** Clock-skew allowance either side of the send window. */
export const MATCH_SKEW_MS = 30_000;

/**
 * Find the order a send with an unknown outcome may have created. The blotter has no
 * Idempotency-Key, so this matches on what it does carry:
 *   contest + position (liquidity-hash tail, else title) + venue (the practice book for paper)
 *   + real/paper + amount (stake or quoted cost, ±1¢ / 2%) + created inside the send window,
 * ignoring market-maker fills and orders already seen in this ticket.
 * Returns every match; the caller treats more than one as "can't tell".
 */
export function matchSentOrder(
  rows: OrderRowLike[],
  send: SendSnapshot,
  opts: { now: number; exclude?: ReadonlySet<string> },
): OrderRowLike[] {
  const key = positionKeyOf(send.liquidityHash);
  const lo = send.at - MATCH_SKEW_MS;
  const hi = opts.now + MATCH_SKEW_MS;
  const amounts = [send.stake.unit === "amount" ? send.stake.value : null, send.cost].filter((n): n is number => n != null && n > 0);
  return rows.filter((r) => {
    if (opts.exclude?.has(r.router_order_id)) return false;
    if (r.mm_strategy_id) return false;
    if (send.contestId && r.contest_id && r.contest_id !== send.contestId) return false;
    const rk = positionKeyOf(r.liquidity_hash);
    if (key && rk) {
      if (rk !== key) return false;
    } else if (send.title && r.title && r.title !== send.title) return false;
    const real = r.funding?.is_real_money ?? ((r.currency ?? "USD").toUpperCase() !== "ATLAS");
    if (real !== (send.mode === "real")) return false;
    if (send.mode === "real") {
      if (r.partner_id && r.partner_id !== send.partnerId) return false;
    } else if (r.partner_id && r.partner_id !== send.partnerId && r.partner_id !== send.practiceBookId && r.partner?.internal_book !== true) {
      return false;
    }
    if (r.amount != null && amounts.length && !amounts.some((a) => Math.abs((r.amount as number) - a) <= Math.max(0.011, a * 0.02))) return false;
    const t = Date.parse(r.created_at ?? r.placed_datetime ?? "");
    if (!Number.isFinite(t) || t < lo || t > hi) return false;
    return true;
  });
}

/** Lookup cadence after an unknown outcome: ms after the failure, then give up. */
export const LOOKUP_SCHEDULE_MS = [1_000, 3_000, 6_000, 10_000, 16_000] as const;

/* ============================================================ the derive */

/**
 * SheetState + greenbook state → everything the ticket renders. `canTradeReal` only changes
 * which fix `not_paper_bettable` offers.
 */
export function deriveTicketView(input: TicketInput, canTradeReal = true): TicketVM {
  const s = input.sheet;
  const mode: Mode = s.mode;
  const v = s.venue;
  const loading = s.loading && !input.everLoaded;

  const venueName = v?.name ?? input.clicked.venueName;
  const subParts = [input.clicked.sideKey?.toUpperCase(), input.clicked.marketLabel].filter(Boolean);
  const header = {
    title: input.clicked.title,
    sub: subParts.length ? subParts.join(" · ") : null,
    venueName,
    venueLogoUrl: v?.logoUrl ?? input.clicked.venueLogoUrl ?? null,
    priceText: v && input.everLoaded ? fmtPrice(v.price, v.americanOdds, input.oddsFormat) : fmtPrice(input.clicked.price, input.clicked.americanOdds ?? null, input.oddsFormat),
  };

  let connection: TicketVM["connection"] = null;
  if (mode === "real" && v && input.everLoaded) {
    const r = readinessOf(v, input.readinessOf);
    connection =
      r.readiness === "ready"
        ? { readiness: "ready", text: `Connected · ${fmtBalance(r.balance, v.account.currency ?? "USD")}`, fix: null }
        : r.readiness === "fund"
          ? { readiness: "fund", text: "Connected · no funds", fix: { label: "Add funds", action: { kind: "venueAccounts" }, external: true } }
          : { readiness: "connect", text: "Not connected", fix: { label: "Connect", action: { kind: "venueAccounts" }, external: true } };
  }

  let hint: TicketVM["hint"] = null;
  if (s.hint && s.hint.venue.partnerId !== v?.partnerId) {
    const hv = s.hint.venue;
    if (mode === "paper" || readinessOf(hv, input.readinessOf).readiness === "ready") {
      hint = { text: `${hv.name} has it at ${fmtPrice(hv.price, hv.americanOdds, input.oddsFormat)}`, fix: { label: "Switch", action: { kind: "select", partnerId: hv.partnerId } } };
    }
  }

  const stakeNum = parseStake(input.stakeText);
  const chipsSrc = input.unit === "amount" ? AMOUNT_CHIPS : SHARE_CHIPS;
  const chips: ChipVM[] = chipsSrc.map((n) => ({
    label: input.unit === "amount" ? `$${n}` : `${n}`,
    value: n,
    active: stakeNum != null && Math.abs(stakeNum - n) < 1e-9,
  }));

  // ── the send, the result, the unknown outcome ──
  const send = input.send;
  let overlay: OverlayVM | null = null;
  if (send) {
    const atLabel = `At ${send.venueName}`;
    const lookup = input.lookup;
    if (lookup) {
      if (lookup.status === "found") {
        const res = resultFromRow(lookup.row, send, input.oddsFormat);
        overlay = { steps: resultSteps(atLabel, res), result: res };
      } else if (lookup.status === "checking") {
        overlay = {
          steps: [{ label: "Sending", state: "done" }, { label: atLabel, state: "unknown" }, { label: "Checking", state: "active" }],
          result: { kind: "checking", headline: "Checking whether your order went through…" },
        };
      } else {
        overlay = {
          steps: [{ label: "Sending", state: "done" }, { label: atLabel, state: "unknown" }, { label: "Unknown", state: "failed" }],
          result: { kind: "unresolved", headline: "We couldn't confirm your order", note: lookup.note },
        };
      }
    } else if (s.phase === "placing" || !s.result) {
      overlay = {
        steps: [
          { label: "Sending", state: input.sendStep === 0 ? "active" : "done" },
          { label: atLabel, state: input.sendStep === 1 ? "active" : "todo" },
          { label: "Result", state: "todo" },
        ],
        result: null,
      };
    } else {
      const res = resultFromSheet(input);
      if (res) overlay = { steps: resultSteps(atLabel, res), result: res };
    }
  }

  // ── problems (the container gates the stake/price ones while typing) ──
  const problems = deriveProblems(input, canTradeReal);

  // ── the button ──
  const q = s.quote;
  const pay = q ? fmtMoney(payWin(q).pay) : null;
  const priceInvalid = input.priceText != null && parseCents(input.priceText) == null;
  let button: ButtonVM;
  if (loading) button = { label: "Getting the price…", tone: mode, disabled: true, spinner: true };
  else if (stakeNum == null) button = { label: "Enter a stake", tone: mode, disabled: true, spinner: false };
  else {
    const base = mode === "real" ? `Place real order${pay ? ` · ${pay}` : ""} on ${venueName}` : `Place paper order${pay ? ` · ${pay}` : ""}`;
    button = { label: base, tone: mode, disabled: !s.canPlace || priceInvalid || !!overlay, spinner: false };
  }

  // ── the REAL MONEY card (the SDK's confirming phase) ──
  let confirm: ConfirmVM | null = null;
  if (s.phase === "confirming" && q) {
    const pw = payWin(q);
    confirm = {
      spend: `You're spending ${fmtMoney(pw.pay)} on ${venueName}`,
      outcome: input.clicked.title,
      priceText: fmtPrice(v?.price ?? q.price, v?.americanOdds ?? null, input.oddsFormat),
      win: `Could win ${fmtMoney(pw.win)} (${q.profit >= 0 ? "+" : "−"}${fmtMoney(Math.abs(q.profit))})`,
      confirmLabel: `Confirm ${fmtMoney(pw.pay)}`,
    };
  }

  const tick = v?.constraints?.price.tick ?? 0.01;
  const autoMax = s.maxPrice;
  const priceText = input.priceText ?? (autoMax != null ? centsText(autoMax) : "");
  const orderType = s.orderType;
  const summaryBits = [
    input.unit === "shares" ? "shares" : null,
    input.priceText != null && autoMax != null ? `max ${fmtCents(parseCents(input.priceText) ?? autoMax)}` : null,
    orderType === "limit" ? "limit" : null,
    input.verifyLiquidity ? "verify" : null,
    s.venues.length > 1 ? `${s.venues.length} venues` : null,
  ].filter(Boolean);

  return {
    mode,
    header,
    connection,
    hint,
    loading,
    stake: {
      unit: input.unit,
      text: input.stakeText,
      label: input.unit === "amount" ? "Stake" : "Contracts",
      chips,
      disabled: !!overlay,
    },
    quote: quoteVM(input),
    options: {
      summary: summaryBits.join(" · "),
      priceText,
      priceLabel: orderType === "limit" ? "Limit price" : "Max price",
      priceHint: v?.price != null ? `now ${fmtCents(v.price)}${input.priceText == null && orderType === "market" ? " · auto" : ""}${tick < 0.01 ? ` · ${fmtCents(tick)} steps` : ""}` : null,
      priceInvalid,
      priceIsAuto: input.priceText == null,
      canLimit: input.canLimit,
      orderType,
      orderTypeCopy:
        orderType === "market"
          ? "Fills what's there up to your max price and cancels the rest."
          : `Rests at ${autoMax != null ? fmtCents(autoMax) : "your price"} on ${venueName} until it fills or you cancel.`,
      verifyLiquidity: input.verifyLiquidity,
      venues: venueRows(input),
    },
    problems,
    button,
    confirm,
    overlay,
    locked: !!overlay,
  };
}

function resultSteps(atLabel: string, r: ResultVM): StepVM[] {
  const label =
    r.kind === "filled" ? "Filled" : r.kind === "resting" ? "Resting" : r.kind === "partial" ? "Partial" : r.kind === "declined" ? "Declined" : r.kind === "cancelled" ? "Cancelled" : "Result";
  const bad = r.kind === "declined";
  return [
    { label: "Sending", state: "done" },
    { label: atLabel, state: bad ? "failed" : "done" },
    { label, state: r.kind === "resting" ? "active" : bad ? "failed" : "done" },
  ];
}

function venueRows(input: TicketInput): VenueRowVM[] {
  const s = input.sheet;
  return [...s.venues]
    .sort((a, b) => {
      const ab = a.eligibility.status === "blocked" ? 1 : 0;
      const bb = b.eligibility.status === "blocked" ? 1 : 0;
      if (ab !== bb) return ab - bb;
      return (a.price ?? 2) - (b.price ?? 2);
    })
    .map((v) => {
      const blocked = v.eligibility.status === "blocked";
      const r = s.mode === "real" ? readinessOf(v, input.readinessOf) : null;
      return {
        partnerId: v.partnerId,
        name: v.name,
        logoUrl: v.logoUrl,
        priceText: fmtPrice(v.price, v.americanOdds, input.oddsFormat),
        best: v.best,
        selected: v.partnerId === s.venue?.partnerId,
        blocked,
        reason: blocked ? (v.eligibility.issues[0] ? shortReason(v.eligibility.issues[0], v) : "Can't take this order") : null,
        readiness: r?.readiness ?? null,
        balanceText: r && r.readiness === "ready" ? fmtBalance(r.balance, v.account.currency ?? "USD") : null,
      };
    });
}

/** One line for a greyed-out venue row. */
function shortReason(i: Issue, v: VenueOption): string {
  switch (i.code) {
    case "venue_disabled":
      return "Not connected";
    case "orders_not_supported":
      return "Prices only — no orders";
    case "no_quote":
    case "no_viable_venues":
      return "No price right now";
    case "scope_not_granted":
      return "Trading not allowed";
    case "order_type_unsupported":
      return "Doesn't take this order type";
    case "insufficient_balance":
      return `No funds on ${v.name}`;
    default:
      return stripDot(i.message);
  }
}

/* ============================================================ the facts */

/** The send snapshot, taken at the tap. */
export function snapshotSend(state: SheetView, contestId: string | null, title: string | null, now: number): SendSnapshot | null {
  const v = state.venue;
  const q = state.quote;
  if (!v || !q || state.stake.value == null) return null;
  return {
    at: now,
    mode: state.mode,
    partnerId: v.partnerId,
    venueName: v.name,
    liquidityHash: v.liquidityHash,
    contestId,
    title,
    stake: { unit: state.stake.unit, value: state.stake.value },
    cost: q.cost,
    contracts: q.contracts,
    maxPrice: q.maxPrice,
    practiceBookId: (state.account as AccountFacts | null)?.practiceWallet.partnerId ?? null,
  };
}
